const aboutCarouselTagName = 'jesoli-about-carousel';
const aboutCarouselSelector = `${aboutCarouselTagName}[data-section-id]`;

class JesoliAboutCarousel extends HTMLElement {
  #abortController;
  #activeIndex = 0;
  #autoplayTimer;
  #hasFocus = false;
  #initialized = false;
  #isHovered = false;
  #isTouching = false;
  #motionQuery;
  #pointerStartY;
  #pointerStartX;

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.destroy();
  }

  initialize() {
    if (this.#initialized) return;

    this.#initialized = true;
    this.#abortController = new AbortController();
    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const { signal } = this.#abortController;

    this.querySelector('[data-about-previous]')?.addEventListener('click', this.#handlePrevious, { signal });
    this.querySelector('[data-about-next]')?.addEventListener('click', this.#handleNext, { signal });
    for (const dot of this.#dots) dot.addEventListener('click', this.#handleDot, { signal });

    const viewport = this.querySelector('[data-about-viewport]');
    viewport?.addEventListener('pointerenter', this.#handlePointerEnter, { signal });
    viewport?.addEventListener('pointerleave', this.#handlePointerLeave, { signal });
    viewport?.addEventListener('focusin', this.#handleFocusIn, { signal });
    viewport?.addEventListener('focusout', this.#handleFocusOut, { signal });
    viewport?.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    viewport?.addEventListener('pointerup', this.#handlePointerUp, { signal });
    viewport?.addEventListener('pointercancel', this.#clearPointer, { signal });

    this.#motionQuery.addEventListener('change', this.#handleMotionChange, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });

    this.#select(0, { announce: false, restartAutoplay: false });
    this.#startAutoplay();
  }

  destroy() {
    if (!this.#initialized) return;

    this.#stopAutoplay();
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#motionQuery = undefined;
    this.#hasFocus = false;
    this.#isHovered = false;
    this.#isTouching = false;
    this.#pointerStartX = undefined;
    this.#pointerStartY = undefined;
    this.#initialized = false;
  }

  get #slides() {
    return [...this.querySelectorAll('[data-about-slide]')].filter((slide) => slide instanceof HTMLElement);
  }

  get #dots() {
    return [...this.querySelectorAll('[data-about-dot]')].filter((dot) => dot instanceof HTMLButtonElement);
  }

  #handlePrevious = (event) => {
    this.#releasePointerFocus(event);
    this.#select(this.#activeIndex - 1, { announce: true, restartAutoplay: true });
  };

  #handleNext = (event) => {
    this.#releasePointerFocus(event);
    this.#select(this.#activeIndex + 1, { announce: true, restartAutoplay: true });
  };

  #handleDot = (event) => {
    const dot = event.currentTarget;
    if (!(dot instanceof HTMLButtonElement)) return;

    this.#releasePointerFocus(event);
    this.#select(Number(dot.dataset.aboutDot), { announce: true, restartAutoplay: true });
  };

  #releasePointerFocus(event) {
    if (event.detail > 0 && event.currentTarget instanceof HTMLElement) event.currentTarget.blur();
  }

  #handleFocusOut = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    this.#hasFocus = false;
    this.#startAutoplay();
  };

  #handleFocusIn = () => {
    this.#hasFocus = true;
    this.#stopAutoplay();
  };

  #handlePointerEnter = (event) => {
    if (event.pointerType === 'touch') return;
    this.#isHovered = true;
    this.#stopAutoplay();
  };

  #handlePointerLeave = (event) => {
    if (event.pointerType === 'touch') return;
    this.#isHovered = false;
    this.#startAutoplay();
  };

  #handlePointerDown = (event) => {
    if (event.pointerType !== 'touch' || !(event.target instanceof Element) || event.target.closest('button')) return;
    this.#isTouching = true;
    this.#pointerStartX = event.clientX;
    this.#pointerStartY = event.clientY;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    this.#stopAutoplay();
  };

  #handlePointerUp = (event) => {
    if (event.pointerType !== 'touch' || this.#pointerStartX === undefined) return;

    const distanceX = event.clientX - this.#pointerStartX;
    const distanceY = event.clientY - (this.#pointerStartY ?? event.clientY);
    this.#pointerStartX = undefined;
    this.#pointerStartY = undefined;
    this.#isTouching = false;
    if (Math.abs(distanceX) >= 40 && Math.abs(distanceX) > Math.abs(distanceY)) {
      this.#select(this.#activeIndex + (distanceX < 0 ? 1 : -1), { announce: true, restartAutoplay: true });
    } else {
      this.#startAutoplay();
    }
  };

  #clearPointer = () => {
    this.#pointerStartX = undefined;
    this.#pointerStartY = undefined;
    this.#isTouching = false;
    this.#startAutoplay();
  };

  #handleMotionChange = () => {
    if (this.#motionQuery?.matches) this.#stopAutoplay();
    else this.#startAutoplay();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) this.#stopAutoplay();
    else this.#startAutoplay();
  };

  #handleBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;

    const slide = this.#slides.find((item) => item.dataset.blockId === event.detail?.blockId);
    if (!slide) return;

    this.#stopAutoplay();
    this.#select(Number(slide.dataset.slideIndex), { announce: false, restartAutoplay: false });
  };

  #select(index, { announce, restartAutoplay }) {
    const slides = this.#slides;
    if (!slides.length) return;

    const normalizedIndex = (index + slides.length) % slides.length;
    this.#activeIndex = normalizedIndex;

    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === normalizedIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', `${!isActive}`);
    });

    this.#dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', `${dotIndex === normalizedIndex}`));

    if (announce) {
      const status = this.querySelector('[data-about-status]');
      if (status) status.textContent = `Image ${normalizedIndex + 1} of ${slides.length}`;
    }

    if (restartAutoplay) {
      this.#stopAutoplay();
      this.#startAutoplay();
    }
  }

  #startAutoplay = () => {
    if (
      this.#autoplayTimer ||
      this.#slides.length < 2 ||
      this.dataset.autoplay !== 'true' ||
      this.#motionQuery?.matches ||
      this.#hasFocus ||
      this.#isHovered ||
      this.#isTouching ||
      document.hidden
    ) {
      return;
    }

    const interval = Number(this.dataset.autoplayInterval) || 6000;
    this.#autoplayTimer = window.setInterval(() => {
      this.#select(this.#activeIndex + 1, { announce: false, restartAutoplay: false });
    }, interval);
  };

  #stopAutoplay = () => {
    window.clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = undefined;
  };
}

if (!customElements.get(aboutCarouselTagName)) {
  customElements.define(aboutCarouselTagName, JesoliAboutCarousel);
}

const getAboutCarousels = (target) => {
  if (!(target instanceof Element)) return [];

  const carousels = [...target.querySelectorAll(aboutCarouselSelector)];
  if (target.matches(aboutCarouselSelector)) carousels.unshift(target);
  return carousels;
};

document.addEventListener('shopify:section:load', (event) => {
  for (const carousel of getAboutCarousels(event.target)) carousel.initialize?.();
});

document.addEventListener('shopify:section:unload', (event) => {
  for (const carousel of getAboutCarousels(event.target)) carousel.destroy?.();
});
